import { useEffect, useState, useRef, useCallback } from 'react'
import { api } from '../../lib/api'
import { useAuthStore } from '../../store/auth'
import { useToast } from '../ui/Toast'
import { Button } from '../ui/Button'
import type { Attachment } from '@shared/types'

interface Props {
  siteId: string
}

export function SitePhotosTab({ siteId }: Props) {
  const [photos, setPhotos] = useState<Attachment[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { toastError } = useToast()

  const loadPhotos = useCallback(() => {
    api.get<Attachment[]>(`/attachments?entity_type=site_photo&entity_id=${encodeURIComponent(siteId)}`)
      .then(setPhotos)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [siteId])

  useEffect(() => { loadPhotos() }, [loadPhotos])

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return
    setUploading(true)
    const token = useAuthStore.getState().token
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('entity_type', 'site_photo')
        formData.append('entity_id', siteId)
        const resp = await fetch('/api/attachments', {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: formData,
        })
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}))
          throw new Error((err as Record<string, string>).detail || `Upload failed (${resp.status})`)
        }
      }
      loadPhotos()
    } catch (err) { toastError('Failed to upload photo', err) }
    finally { setUploading(false) }
  }

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={e => handleUpload(e.target.files)}
      />
      <div className="flex justify-between items-center" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 18, fontWeight: 700 }}>Site Photos</h3>
        <Button loading={uploading} onClick={() => fileInputRef.current?.click()}>Upload Photo</Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="skeleton" style={{ height: 160, borderRadius: 'var(--radius-lg)' }} />
          ))}
        </div>
      ) : photos.length > 0 ? (
        <div className="grid grid-cols-4 gap-4">
          {photos.map(photo => (
            <div key={photo.id} className="card" style={{ padding: 0, overflow: 'hidden', cursor: 'pointer' }}>
              <div style={{
                height: 120, background: 'var(--bg-inset)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 24, color: 'var(--text-tertiary)',
              }}>📷</div>
              <div style={{ padding: '8px 12px' }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {photo.file_name || 'Untitled photo'}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>
                  {new Date(photo.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📸</div>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>No photos yet</div>
          <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 16 }}>
            Upload photos from site walks, installation documentation, and equipment closeups.
          </p>
          <Button loading={uploading} onClick={() => fileInputRef.current?.click()}>Upload Photo</Button>
        </div>
      )}
    </div>
  )
}
